import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { api, ClientApiError, type ClientWithdrawal } from '../api'
import { tradingServerLabel } from '../branding'
import { fixtureAccount, fixtureSummary, useFixturesFromQuery } from '../fixtures'
import { useAppPath } from '../nav'
import type { AccountSummary, TradingAccount } from '../types'
import { money } from '../prediction-filter'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { LiveRequiredPanel } from '../components/LiveRequiredPanel'

type TimelineEvent = {
  event_type?: string
  timestamp?: string
  description?: string
}

type StatusTone =
  | 'pending'
  | 'review'
  | 'approved'
  | 'rejected'
  | 'processing'
  | 'completed'
  | 'neutral'

function safeError(err: unknown, fallback: string): string {
  if (err instanceof ClientApiError) {
    const msg = (err.message || '').trim()
    if (!msg) return fallback
    const lower = msg.toLowerCase()
    if (
      lower.includes('internal') ||
      lower.includes('exception') ||
      lower.includes('traceback') ||
      lower === 'invalid request' ||
      lower.includes('stack') ||
      lower.includes('mt5manager') ||
      lower.includes('pip install') ||
      lower.includes('\\downloads\\') ||
      lower.includes('/downloads/') ||
      lower.includes('.whl') ||
      lower.includes('package is not installed') ||
      lower.includes('metaquotes') ||
      lower.includes('account creation disabled') ||
      lower.includes('mt5_allow_account_creation')
    ) {
      return fallback
    }
    return msg
  }
  if (err instanceof Error && err.message) {
    const lower = err.message.toLowerCase()
    if (
      lower.includes('internal') ||
      lower.includes('failed to fetch') ||
      lower.includes('mt5manager') ||
      lower.includes('pip install') ||
      lower.includes('package is not installed')
    ) {
      return fallback
    }
    return err.message
  }
  return fallback
}

function isSelectableAccount(account: TradingAccount): boolean {
  const status = String(account.status || '').toUpperCase()
  return status === 'ACTIVE' || status === ''
}

function maskLogin(login: number | string | null | undefined): string {
  const text = String(login ?? '').trim()
  if (!text) return '—'
  if (text.length <= 2) return `**${text}`
  return `***${text.slice(-2)}`
}

function statusTone(raw?: string | null): StatusTone {
  const s = String(raw || '').toUpperCase()
  if (!s) return 'neutral'
  if (/(REJECT|DENIED)/.test(s)) return 'rejected'
  if (/(COMPLETE|SETTLED|PAID)/.test(s)) return 'completed'
  if (/(APPROVE)/.test(s)) return 'approved'
  if (/(PROCESS)/.test(s)) return 'processing'
  if (/(REVIEW)/.test(s)) return 'review'
  if (/(PEND|SUBMIT|CREATED|OPEN)/.test(s)) return 'pending'
  return 'pending'
}

function statusLabel(raw?: string | null): string {
  const s = String(raw || '').toUpperCase()
  const map: Record<string, string> = {
    PENDING: 'Pending',
    DEBIT_PENDING: 'Debiting',
    PENDING_ADMIN_REVIEW: 'Pending Review',
    UNDER_REVIEW: 'Under Review',
    APPROVED: 'Approved',
    REJECTING: 'Rejecting',
    REJECTED: 'Rejected',
    REJECTED_AND_REFUNDED: 'Rejected & Refunded',
    RECONCILIATION_REQUIRED: 'Needs Review',
    PROCESSING: 'Processing',
    COMPLETED: 'Completed',
  }
  if (map[s]) return map[s]
  if (!s) return '—'
  return s
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function formatWhen(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

function StatusBadge({ status }: { status?: string | null }) {
  return (
    <span className={`cp-wd__status cp-wd__status--${statusTone(status)}`}>
      {statusLabel(status)}
    </span>
  )
}

function availableFromSummary(summary?: AccountSummary | null, account?: TradingAccount | null): number | null {
  if (summary?.free_margin != null && Number.isFinite(Number(summary.free_margin))) {
    return Number(summary.free_margin)
  }
  if (summary?.balance != null && Number.isFinite(Number(summary.balance))) {
    return Number(summary.balance)
  }
  if (account?.balance != null && Number.isFinite(Number(account.balance))) {
    return Number(account.balance)
  }
  return null
}

export function WithdrawalsPage() {
  const appPath = useAppPath()
  const fixtures = useFixturesFromQuery()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [accounts, setAccounts] = useState<TradingAccount[]>([])
  const [summaries, setSummaries] = useState<Record<number, AccountSummary | null>>({})
  const [accountId, setAccountId] = useState('')
  const [amount, setAmount] = useState('50.00')
  const [destination, setDestination] = useState('USDT TRC20 Wallet')
  const [wallet, setWallet] = useState('')
  const [note, setNote] = useState('')
  const [history, setHistory] = useState<ClientWithdrawal[]>([])
  const [submitted, setSubmitted] = useState<ClientWithdrawal | null>(null)
  const [detailId, setDetailId] = useState<number | null>(null)
  const [detail, setDetail] = useState<ClientWithdrawal | null>(null)
  const [timeline, setTimeline] = useState<TimelineEvent[]>([])
  const [detailWarning, setDetailWarning] = useState('')
  const [detailLoading, setDetailLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const selectable = useMemo(() => accounts.filter(isSelectableAccount), [accounts])
  const liveSelectable = useMemo(
    () =>
      selectable.filter((a) => {
        const m = String(a.mode || '').toUpperCase()
        return m === 'LIVE' || m === 'REAL'
      }),
    [selectable],
  )
  const hasAccount = selectable.length > 0
  const hasLiveAccount = liveSelectable.length > 0
  const selected = liveSelectable.find((a) => String(a.id) === accountId) || liveSelectable[0] || null
  const selectedId = selected?.id ?? null
  const selectedSummary = selected ? summaries[selected.id] : null

  const load = useCallback(async () => {
    if (fixtures) {
      setAccounts([fixtureAccount])
      setAccountId(String(fixtureAccount.id))
      setSummaries({ [fixtureAccount.id]: fixtureSummary })
      setHistory([])
      return
    }
    const [accRes, wdRes] = await Promise.all([
      api.listAccounts(),
      api.listWithdrawals().catch(() => ({ success: false as const, withdrawals: [] as ClientWithdrawal[] })),
    ])
    const list = (accRes.accounts || []).map((a) => ({
      ...a,
      mt5Login: a.mt5Login ?? (a as { mt5_login?: number }).mt5_login,
    }))
    setAccounts(list)
    setHistory(wdRes.withdrawals || [])

    const active = list.filter(isSelectableAccount)
    setAccountId((prev) => {
      if (prev && active.some((a) => String(a.id) === prev)) {
        const m = String(active.find((a) => String(a.id) === prev)?.mode || '').toUpperCase()
        if (m === 'LIVE' || m === 'REAL') return prev
      }
      const livePreferred = active.find((a) => {
        const m = String(a.mode || '').toUpperCase()
        return m === 'LIVE' || m === 'REAL'
      })
      return livePreferred ? String(livePreferred.id) : active[0] ? String(active[0].id) : ''
    })
    // Do not await MT5 summaries here — bridge latency was blocking the whole page
    // (Loading… forever, Available —, form hidden). Balance loads in the background.
  }, [fixtures])

  useEffect(() => {
    let cancelled = false
    async function run() {
      setLoading(true)
      setError('')
      try {
        await load()
      } catch (err) {
        if (!cancelled) setError(safeError(err, 'Unable to load withdrawal information.'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [load])

  useEffect(() => {
    if (fixtures || !selectedId) return
    let cancelled = false
    void api
      .summary(selectedId)
      .then((s) => {
        if (!cancelled) {
          setSummaries((prev) => ({ ...prev, [selectedId]: s.account }))
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSummaries((prev) => ({ ...prev, [selectedId]: null }))
        }
      })
    return () => {
      cancelled = true
    }
  }, [selectedId, fixtures])

  async function submitWithdrawal(e: FormEvent) {
    e.preventDefault()
    setError('')
    setNotice('')
    if (!selectedId) {
      setError('Select an active trading account.')
      return
    }
    if (!amount.trim() || !wallet.trim()) {
      setError('Amount and USDT TRC20 wallet are required.')
      return
    }
    setConfirmOpen(true)
  }

  async function confirmSubmitWithdrawal() {
    if (!selectedId) return
    setSubmitting(true)
    setError('')
    setNotice('')
    try {
      const res = await api.createWithdrawal({
        tradingAccountId: selectedId,
        amount: amount.trim(),
        destination: destination.trim() || 'USDT TRC20 Wallet',
        walletAddress: wallet.trim(),
        note: note.trim() || undefined,
        availableBalance: available != null ? String(available) : undefined,
      })
      setConfirmOpen(false)
      setSubmitted(res.withdrawal)
      setAmount('')
      setWallet('')
      setNote('')
      setNotice(
        `Withdrawal ${res.withdrawal.reference} submitted. MT5 was debited; request is pending admin review.`,
      )
      await load()
    } catch (err) {
      setError(safeError(err, 'Unable to submit withdrawal.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function openDetail(id: number) {
    setDetailId(id)
    setDetail(null)
    setTimeline([])
    setDetailWarning('')
    setDetailLoading(true)
    setError('')
    try {
      const res = await api.getWithdrawal(id)
      setDetail(res.withdrawal)
      setTimeline(res.timeline || [])
      setDetailWarning(res.warning || '')
    } catch (err) {
      setError(safeError(err, 'Unable to load withdrawal details.'))
      setDetailId(null)
    } finally {
      setDetailLoading(false)
    }
  }

  const available = availableFromSummary(selectedSummary, selected)
  const currency = selectedSummary?.currency || selected?.currency || 'USD'
  const login = selectedSummary?.mt5_login ?? selected?.mt5Login

  return (
    <section className="cp-page cp-wd">
      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading…
        </p>
      ) : null}
      {error ? (
        <div className="cp-wd__alert cp-wd__alert--error" role="alert">
          <p>{error}</p>
          <Button type="button" variant="ghost" size="sm" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : null}
      {notice ? <p className="cp-ok">{notice}</p> : null}

      {!loading && !hasAccount ? (
        <div className="cp-wd__blocked">
          <span className="cp-badge cp-badge--mt5">Powered by MT5</span>
          <h2>Trading account required</h2>
          <p>
            Open a Demo account to practice, or create a Live account under Trading Accounts to
            withdraw real funds.
          </p>
          <div className="cp-wd__blocked-actions">
            <Button to={appPath('/app/accounts')} variant="primary">
              View Trading Accounts
            </Button>
          </div>
        </div>
      ) : null}

      {!loading && hasAccount && !hasLiveAccount ? (
        <LiveRequiredPanel kind="withdrawal" />
      ) : null}

      {loading || hasLiveAccount ? (
        <>
          {!loading && submitted ? (
            <section className="cp-wd__receipt" aria-live="polite">
              <div className="cp-wd__panel-head">
                <h2>Request submitted</h2>
                <StatusBadge status={submitted.status} />
              </div>
              <dl className="cp-wd__receipt-grid">
                <div>
                  <dt>Reference</dt>
                  <dd>{submitted.reference}</dd>
                </div>
                <div>
                  <dt>Requested Amount</dt>
                  <dd>
                    {money(submitted.amount)} {submitted.currency || currency}
                  </dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>
                    <StatusBadge status={submitted.status} />
                  </dd>
                </div>
                <div>
                  <dt>Submitted Time</dt>
                  <dd>{formatWhen(submitted.submittedAt)}</dd>
                </div>
                <div>
                  <dt>Expected Review</dt>
                  <dd>{submitted.expectedReview || 'Usually within 1–2 business days'}</dd>
                </div>
              </dl>
              <p className="cp-muted">
                Your Live MT5 balance was debited at submit. Payout requires admin review — no automatic
                blockchain transfer.
              </p>
              <Button type="button" variant="ghost" size="sm" onClick={() => setSubmitted(null)}>
                New request
              </Button>
            </section>
          ) : (
            <section className={`cp-wd__panel${loading ? ' is-refreshing' : ''}`}>
              <div className="cp-wd__panel-head">
                <div>
                  <h2>Withdraw Funds</h2>
                  <p className="cp-wd__panel-sub">Live account withdrawal</p>
                </div>
                <p className="cp-wd__available-chip">
                  <span>Available</span>
                  <strong className="cp-num">
                    {!loading && available != null ? `${money(available)} ${currency}` : '—'}
                  </strong>
                </p>
              </div>
              {!loading ? (
              <form className="cp-wd__form" onSubmit={(e) => void submitWithdrawal(e)}>
                <div className="cp-wd__form-grid">
                  {liveSelectable.length > 1 ? (
                    <label className="cp-field cp-wd__form-span">
                      Trading Account
                      <select
                        value={accountId}
                        onChange={(e) => setAccountId(e.target.value)}
                        required
                      >
                        {liveSelectable.map((a) => (
                          <option key={a.id} value={a.id}>
                            {maskLogin(a.mt5Login)} · {tradingServerLabel(a.mode, a.server)}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <p className="cp-wd__account-line cp-wd__form-span">
                      <span>Trading Account</span>
                      <strong>
                        {maskLogin(login)} · {tradingServerLabel(selected?.mode, selected?.server)}
                      </strong>
                    </p>
                  )}
                  <label className="cp-field">
                    Amount (USD)
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0.01"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                    />
                  </label>
                  <label className="cp-field">
                    Destination
                    <select value={destination} onChange={(e) => setDestination(e.target.value)}>
                      <option value="USDT TRC20 Wallet">USDT TRC20 Wallet</option>
                    </select>
                  </label>
                  <label className="cp-field cp-wd__form-span">
                    Wallet Address
                    <input
                      type="text"
                      autoComplete="off"
                      spellCheck={false}
                      value={wallet}
                      onChange={(e) => setWallet(e.target.value)}
                      placeholder="Paste destination wallet"
                      required
                    />
                  </label>
                  <label className="cp-field cp-wd__form-span">
                    Optional Note
                    <input
                      type="text"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Short note for reviewers (optional)"
                    />
                  </label>
                </div>
                <div className="cp-wd__actions">
                  <p className="cp-muted cp-wd__hint" role="status">
                    Debited from Live MT5 on submit · Admin review before USDT payout
                  </p>
                  <Button type="submit" variant="primary" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit Withdrawal Request'}
                  </Button>
                </div>
              </form>
              ) : null}
            </section>
          )}

          <section className={`cp-wd__history${loading ? ' is-refreshing' : ''}`}>
            <div className="cp-wd__section-head">
              <h2>Recent Withdrawals</h2>
            </div>
            {!loading && history.length > 0 ? (
              <>
                <div className="cp-wd__table-wrap" role="region" aria-label="Withdrawal history">
                  <table className="cp-wd__table">
                    <thead>
                      <tr>
                        <th scope="col">Reference</th>
                        <th scope="col">Amount</th>
                        <th scope="col">Status</th>
                        <th scope="col">Submitted</th>
                        <th scope="col">Completed</th>
                        <th scope="col">View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((row) => (
                        <tr key={row.id}>
                          <td>{row.reference}</td>
                          <td>
                            {money(row.amount)} {row.currency || 'USD'}
                          </td>
                          <td>
                            <StatusBadge status={row.status} />
                          </td>
                          <td>{formatWhen(row.submittedAt)}</td>
                          <td>{formatWhen(row.completedAt)}</td>
                          <td>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => void openDetail(Number(row.id))}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="cp-wd__cards" aria-label="Withdrawal history cards">
                  {history.map((row) => (
                    <article key={`m-${row.id}`} className="cp-wd__card">
                      <div className="cp-wd__card-top">
                        <strong>{row.reference}</strong>
                        <StatusBadge status={row.status} />
                      </div>
                      <p className="cp-wd__card-amount">
                        {money(row.amount)} {row.currency || 'USD'}
                      </p>
                      <p className="cp-muted">{formatWhen(row.submittedAt)}</p>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => void openDetail(Number(row.id))}
                      >
                        View Details
                      </Button>
                    </article>
                  ))}
                </div>
              </>
            ) : !loading ? (
              <p className="cp-muted">No withdrawals yet.</p>
            ) : null}
          </section>
        </>
      ) : null}

      {detailId != null ? (
        <div
          className="cp-wd__drawer-backdrop"
          role="presentation"
          onClick={() => setDetailId(null)}
        >
          <aside
            className="cp-wd__drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Withdrawal details"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-wd__drawer-head">
              <h2>Withdrawal details</h2>
              <Button type="button" variant="ghost" size="sm" onClick={() => setDetailId(null)}>
                Close
              </Button>
            </div>
            {detailLoading ? <p className="cp-muted">Loading…</p> : null}
            {detail ? (
              <>
                <dl className="cp-wd__detail-grid">
                  <div>
                    <dt>Reference</dt>
                    <dd>{detail.reference}</dd>
                  </div>
                  <div>
                    <dt>Account</dt>
                    <dd>#{detail.tradingAccountId}</dd>
                  </div>
                  <div>
                    <dt>Amount</dt>
                    <dd>
                      {money(detail.amount)} {detail.currency || 'USD'}
                    </dd>
                  </div>
                  <div>
                    <dt>Wallet</dt>
                    <dd className="cp-wd__mono">{detail.walletAddress}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>
                      <StatusBadge status={detail.status} />
                    </dd>
                  </div>
                  {detail.rejectionReason ? (
                    <div>
                      <dt>Reason</dt>
                      <dd>{detail.rejectionReason}</dd>
                    </div>
                  ) : null}
                </dl>
                {detailWarning ? <p className="cp-muted">{detailWarning}</p> : null}
                <h3>Timeline</h3>
                <ol className="cp-wd__timeline">
                  {timeline.length === 0 ? <li className="cp-muted">No timeline events yet.</li> : null}
                  {timeline.map((ev, idx) => (
                    <li key={`${ev.timestamp}-${idx}`}>
                      <strong>{ev.event_type || 'Update'}</strong>
                      <span>{formatWhen(ev.timestamp)}</span>
                      {ev.description ? <p>{ev.description}</p> : null}
                    </li>
                  ))}
                </ol>
              </>
            ) : null}
          </aside>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm withdrawal"
        body={
          <>
            <p>
              Debit {amount.trim() || '—'} {currency} from Live account {maskLogin(login)} and submit for
              admin review?
            </p>
            <p className="cp-muted">
              Destination wallet: {wallet.trim() || '—'}. No automatic USDT payout is performed.
            </p>
          </>
        }
        confirmLabel="Confirm & debit"
        busy={submitting}
        onCancel={() => {
          if (!submitting) setConfirmOpen(false)
        }}
        onConfirm={() => {
          void confirmSubmitWithdrawal()
        }}
      />
    </section>
  )
}
